/**
 * Global geometric tolerance in meters (1 µm). Chosen far below any planning-relevant
 * precision so it only absorbs floating point noise.
 */
export const EPS = 1e-6;

export function nearlyEqual(a: number, b: number, eps: number = EPS): boolean {
  return Math.abs(a - b) <= eps;
}

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Rounds to a multiple of `step`. Returns the value unchanged for non-positive steps. */
export function snapToStep(value: number, step: number, origin = 0): number {
  if (!(step > 0)) return value;
  return origin + Math.round((value - origin) / step) * step;
}

export function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

export function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function radToDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

/** Normalizes an angle to the range [0, 2π). */
export function normalizeAngle(rad: number): number {
  const full = Math.PI * 2;
  const r = rad % full;
  return r < 0 ? r + full : r;
}
